import React, { useState } from "react";
import { InlineError } from "@/components/common/InlineError";
import { useNavigate } from "react-router";
import WidgetsOutlinedIcon from "@mui/icons-material/WidgetsOutlined";
import TableChartOutlinedIcon from "@mui/icons-material/TableChartOutlined";
import { notifyRefusal, openModel } from "@/components/canvas/modelSession";
import { ContextMenu } from "@/components/common/ContextMenu";
import { useContextMenu } from "@/components/hooks/useContextMenu";
import { Badge } from "@/components/common/Badge";
import { NumberCount } from "@/components/common/NumberCount";
import { SearchBar } from "@/components/common/SearchBar";
import { StatusChip } from "@/components/common/StatusChip";
import { SectionRule } from "components/common/SectionRule";
import { plural } from "@/components/common/setUtils";
import { MetaAddButton } from "@/components/main/datasets/DatasetMetaSection";
import { ListEmptyShell } from "@/components/main/overview/ListEmptyShell";
import { useOrgContextStore } from "@/components/main/stores/orgContextStore";
import {
  ColumnTopSpacer,
  ListBox,
  ListCount,
} from "@/components/main/pages/pageColumn";
import { useApps } from "@/lib/api/apps/hooks";
import type { AppListItem } from "@/lib/api/apps/types";
import { statusLabel, usd, whenLabel } from "./app-format";
import { NewAppPage } from "./new-app-page";

/** The org's apps (docs/AgentApps.md § 11), each kind its own row: a
 *  design app's rounds and whether it is applied; a research app's status,
 *  schedule, open questions and spend. */
export const AppsPage: React.FC = () => {
  const orgId = useOrgContextStore((s) => s.activeOrgId);
  const navigate = useNavigate();
  const { data, isLoading, error } = useApps(orgId);
  const [filter, setFilter] = useState("");
  const apps = (data ?? []).filter((a) =>
    a.name.toLowerCase().includes(filter.trim().toLowerCase()),
  );

  const loading = isLoading && !data;
  if (!loading && !error && (data ?? []).length === 0)
    return <NewAppPage first />;
  const openQuestions = (data ?? []).reduce(
    (n, a) => n + (a.kind === "research" ? a.open_questions : 0),
    0,
  );

  return (
    <div className="relative flex h-full w-full flex-col items-center overflow-y-auto px-6">
      <ColumnTopSpacer />
      <div
        className="flex w-full flex-col"
        style={{
          maxWidth: "45rem",
          paddingBottom: "2.5rem",
        }}
      >
        {error && <InlineError>{(error as Error).message}</InlineError>}
        {!error && loading && (
          <ListEmptyShell
            icon={<WidgetsOutlinedIcon sx={{ fontSize: 14 }} />}
            label="Applications"
            hint={null}
            loading
            addLabel="New app"
            onAdd={() => navigate("/apps/new")}
            search={{
              value: filter,
              onChange: setFilter,
              placeholder: "Search apps",
            }}
          />
        )}
        {!error && !loading && (
          <>
            <div className="mb-6 shrink-0">
              <SearchBar
                value={filter}
                onChange={setFilter}
                placeholder="Search apps"
                size="lg"
              />
            </div>
            {/* Above the list, as on Overview: the scrollbar starts under the title. */}
            <SectionRule
              inset
              icon={<WidgetsOutlinedIcon sx={{ fontSize: 14 }} />}
              label="Applications"
              count={
                openQuestions > 0 ? (
                  <NumberCount count={openQuestions} size="sm" />
                ) : undefined
              }
            />
            <ListBox rows={12}>
              {apps.map((app) => (
                <AppRow
                  key={app.app_id}
                  app={app}
                  onOpen={() => navigate(`/apps/${app.app_id}`)}
                />
              ))}
              {apps.length === 0 && (
                <div className="py-12 text-center text-small italic text-fg-muted">
                  {`No apps match “${filter.trim()}”.`}
                </div>
              )}
            </ListBox>
            <div className="mt-6 flex shrink-0 items-center">
              <ListCount
                parts={[
                  `${(data ?? []).length} apps`,
                  openQuestions > 0 && `${openQuestions} open questions`,
                ]}
              />
              <span className="ml-auto">
                <MetaAddButton
                  label="New app"
                  onClick={() => navigate("/apps/new")}
                />
              </span>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

/** The kind badge has one width, so the names line up. The menu opens the
 *  model an app works in: a research app's own (its notes and runs, read-only
 *  to its owner), a design app's target, where Apply writes. */
function AppRow({ app, onOpen }: { app: AppListItem; onOpen: () => void }) {
  const navigate = useNavigate();
  const menu = useContextMenu();
  const design = app.kind === "design";
  const modelId = design ? app.target_model_id : app.model_id;
  const openAppModel = async () => {
    if (!modelId) return;
    const result = await openModel(modelId);
    if (result === "ready") navigate(`/canvas/${modelId}`);
    else if (result !== "cancelled") notifyRefusal(result);
  };
  const status = design
    ? app.applied_version
      ? { tone: "accent" as const, text: "Applied" }
      : { tone: "muted" as const, text: "Drafting" }
    : statusLabel(app);
  // A hovered name takes the width it needs and pushes the rest right, where
  // the row clips it, so every row's columns line up the rest of the time.
  return (
    <>
      <button
        type="button"
        onClick={onOpen}
        {...menu.triggerProps}
        className={`group w-full text-left flex items-center gap-3 overflow-hidden px-1 hover:bg-bg-subtle rounded-app cursor-pointer ${menu.isOpen ? "bg-bg-subtle" : ""}`}
        style={{ minHeight: "var(--item-height)" }}
      >
        <span className="flex flex-1 items-center gap-2 min-w-0 group-hover:[flex:1_0_auto]">
          <span className="shrink-0" style={{ width: "4.5rem" }}>
            <Badge label={design ? "Design" : "Research"} size="xs" />
          </span>
          <span className="text-fg truncate group-hover:overflow-visible">
            {app.name}
          </span>
          <span className="shrink-0">
            <StatusChip tone={status.tone}>{status.text}</StatusChip>
          </span>
        </span>
        <span
          className="flex shrink-0 items-center justify-end"
          style={{ width: "2rem" }}
        >
          {!design && app.open_questions > 0 && (
            <NumberCount count={app.open_questions} size="sm" />
          )}
        </span>
        <span
          className={`shrink-0 truncate text-right ${design ? "text-fg-muted-regular-small" : "text-mono-small-muted"}`}
          style={{ width: "10rem" }}
        >
          {design
            ? plural(app.rounds, "version")
            : `${usd(app.spent_total_cents)} of ${usd(app.cost_limit_cents)}`}
        </span>
        <span
          className="shrink-0 font-mono text-fg-muted text-right"
          style={{
            width: "7rem",
            fontSize: "var(--font-size-x-small)",
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {whenLabel(
            design ? app.updated_at : (app.last_run_at ?? app.updated_at),
          )}
        </span>
      </button>
      <ContextMenu
        items={[
          {
            label: design ? "Open target model" : "Open app model",
            icon: <TableChartOutlinedIcon className="icon-md" />,
            onClick: () => void openAppModel(),
            disabled: !modelId,
            hint: modelId ? undefined : "None picked yet",
          },
        ]}
        position={menu.position}
        onClose={menu.close}
        ariaLabel={`Actions for ${app.name}`}
      />
    </>
  );
}
